/* Pre-EAS Sprint — public share landing page (/d/:shareId).
 *
 * Visible WITHOUT login. Renders only the server-side whitelist from
 * GET /api/share/public/{share_id}: image, title, price, location,
 * key facts, reliable market position, short DealAlert hint, honest
 * geo-precision note, CTAs. Revoked/unknown links and offline listings
 * degrade gracefully. NO internal ids are exposed — "open in app"
 * resolves the listing server-side for authenticated users only.
 */
import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { MapPin, Sparkles, ExternalLink } from "lucide-react";
import { apiClient } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { PublicAnalysisView } from "../components/share/PublicAnalysisView";

const FACT_ORDER = [
  "object_type", "transaction_type", "rooms", "living_area_m2",
  "price_per_m2", "make", "model", "year", "mileage_km", "fuel",
];

function factValue(t, key, value) {
  if (key === "transaction_type") return t(`public.facts.${value}`, { defaultValue: value });
  if (key === "living_area_m2") return `${value} m²`;
  if (key === "price_per_m2") return `${Math.round(value).toLocaleString("de-AT")} €/m²`;
  if (key === "mileage_km") return `${Number(value).toLocaleString("de-AT")} km`;
  return String(value);
}

export default function PublicSharePage() {
  const { shareId } = useParams();
  const { t } = useTranslation("share");
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-share", shareId],
    queryFn: async () => {
      const r = await apiClient.get(`/share/public/${shareId}`);
      return r.data;
    },
    retry: false,
    staleTime: 60_000,
  });

  const openInApp = async () => {
    if (!user) {
      navigate("/login", { state: { from: { pathname: `/d/${shareId}` } } });
      return;
    }
    try {
      const r = await apiClient.get(`/share/resolve/${shareId}`);
      apiClient
        .post(`/share/public/${shareId}/event`, { type: "app_opened" })
        .catch(() => {});
      navigate(`/listing/${r.data.listing_ulid}`);
    } catch {
      /* revoked meanwhile — stay on page */
    }
  };

  const approx = data && data.vertical === "immo" &&
    !["exact", "street"].includes(data.geo_precision || "");

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <main
        className={`mx-auto ${data?.type === "analysis" ? "max-w-2xl" : "max-w-xl"} px-5 py-8`}
        data-testid="public-share-page"
      >
        {isLoading && (
          <div className="space-y-4 animate-pulse" data-testid="public-share-loading">
            <div className="h-56 rounded-2xl bg-zinc-900" />
            <div className="h-6 w-3/4 rounded bg-zinc-900" />
            <div className="h-6 w-1/3 rounded bg-zinc-900" />
          </div>
        )}

        {isError && (
          <div className="text-center py-16" data-testid="public-share-notfound">
            <h1 className="text-xl font-semibold">{t("public.notFoundTitle")}</h1>
            <p className="mt-3 text-sm text-zinc-400">{t("public.notFoundBody")}</p>
            <Link
              to="/register"
              data-testid="public-share-explore"
              className="mt-6 inline-block rounded-full bg-amber-500 px-6 py-2.5 text-sm font-semibold text-zinc-950 hover:bg-amber-400"
            >
              {t("public.explore")}
            </Link>
          </div>
        )}

        {data && data.type === "analysis" && (
          <PublicAnalysisView data={data} shareId={shareId} />
        )}

        {data && data.type !== "analysis" && (
          <article className="space-y-5">
            <div className="text-[11px] uppercase tracking-[0.14em] text-amber-400">
              {t("public.badge")}
            </div>

            {data.image_url && (
              <img
                src={data.image_url}
                alt={data.title || "Deal"}
                data-testid="public-share-image"
                className="w-full h-64 rounded-2xl object-cover border border-zinc-800"
              />
            )}

            {data.offline && (
              <div
                data-testid="public-share-offline"
                className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
              >
                {t("public.offline")}
              </div>
            )}

            <div>
              <h1 className="text-2xl font-semibold" data-testid="public-share-title">
                {data.title}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span
                  className="text-2xl font-bold text-amber-400"
                  data-testid="public-share-price"
                >
                  {data.price_display}
                </span>
                {data.market_pct_below != null && (
                  <span
                    data-testid="public-share-market"
                    className="rounded-full border border-emerald-400/40 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300"
                  >
                    {t("belowMarket", { pct: data.market_pct_below })}
                  </span>
                )}
              </div>
              {data.location && (
                <div className="mt-2 flex items-center gap-1.5 text-sm text-zinc-400">
                  <MapPin className="h-4 w-4" />
                  {data.location}
                </div>
              )}
            </div>

            {Object.keys(data.facts || {}).length > 0 && (
              <dl className="grid grid-cols-2 gap-3">
                {FACT_ORDER.filter((k) => data.facts[k] != null).map((k) => (
                  <div
                    key={k}
                    className="rounded-xl border border-zinc-800 bg-zinc-950/60 px-3 py-2.5"
                  >
                    <dt className="text-[11px] uppercase tracking-wide text-zinc-500">
                      {t(`public.facts.${k}`)}
                    </dt>
                    <dd className="text-sm font-medium text-zinc-200">
                      {factValue(t, k, data.facts[k])}
                    </dd>
                  </div>
                ))}
              </dl>
            )}

            {data.ai_hint && (
              <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 px-4 py-3">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-emerald-300">
                  <Sparkles className="h-3.5 w-3.5" />
                  {t("public.aiAvailable")}
                </div>
                <p className="mt-1.5 text-sm text-zinc-300" data-testid="public-share-ai-hint">
                  {data.ai_hint}
                </p>
              </div>
            )}

            {approx && (
              <p className="text-xs text-zinc-500" data-testid="public-share-approx">
                {t("public.approxLocation")}
              </p>
            )}

            <p className="text-xs text-zinc-500">{t("public.hint")}</p>

            <div className="flex flex-col gap-2.5 pt-1">
              <button
                type="button"
                onClick={openInApp}
                data-testid="public-share-open-app"
                className="rounded-full bg-amber-500 px-6 py-3 text-sm font-semibold text-zinc-950 hover:bg-amber-400"
              >
                {t("public.openInApp")}
              </button>
              {!user && (
                <Link
                  to={`/register?${new URLSearchParams({
                    ...(data.referral_code ? { ref: data.referral_code } : {}),
                    share: shareId,
                  }).toString()}`}
                  data-testid="public-share-register"
                  className="rounded-full border border-zinc-700 px-6 py-3 text-center text-sm font-medium text-zinc-200 hover:border-amber-500/60"
                >
                  {t("public.registerFree")}
                </Link>
              )}
              {data.source_url && !data.offline && (
                <a
                  href={data.source_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="public-share-source"
                  className="inline-flex items-center justify-center gap-1.5 px-6 py-2 text-sm text-zinc-400 hover:text-zinc-200"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t("public.openSource")}
                </a>
              )}
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
